// src/screens/DispatchScreen.jsx
import React, { useEffect, useState, useMemo, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Tab, Dialog, Transition, Popover } from '@headlessui/react';
import { FiChevronRight, FiFilter, FiMessageSquare } from 'react-icons/fi';
import request from '../utils/request';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SelectDropdown from '../components/SelectDropdown';
import DispatchOrderDrawer from '../components/DispatchOrderDrawer';
import AssignDriver from '../components/AssignDriver';
import { getCoordinates } from '../utils/geocode';
import { formatDate } from '../utils/formatDate';
import ChatPanel from '../components/ChatPanel'
import useLockBodyScroll from '../utils/useLockBodyScroll';
import { XMarkIcon } from '@heroicons/react/24/outline'
import { FiLock, FiClock } from 'react-icons/fi';
import ChangeDeliveryFee from '../components/ChangeDeliveryFee'
import { canBatch } from '../components/Batching';
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { arrivalInPst } from '../utils/propertyTimezoneToPST';
import { StatePill } from '../utils/statePill';

// 2) TopBar: sticky header with search + Filters
function TopBar({ search, setSearch, onFilterClick, onChatClick, unreadCount }) {
  return (
    <header className="flex items-center justify-between px-6 py-4 bg-white border-b shadow-sm sticky top-0 z-20 ml-16">
      <h1 className="text-2xl font-semibold">Dispatch</h1>
      <div className="flex items-center space-x-4">
        <input
          type="search"
          placeholder="Search orders…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-1 border rounded-lg w-64 focus:outline-none"
        />
        <button
          onClick={onFilterClick}
          className="flex items-center px-3 py-1 border rounded-lg hover:bg-gray-100 transition"
        >
          <FiFilter className="h-5 w-5 mr-1" />
          Filters
        </button>
        {/* ← new chat button */}
        <button
          onClick={onChatClick}
          className="relative flex items-center px-3 py-1 border rounded-lg hover:bg-gray-100 transition"
        >
          <FiMessageSquare className="h-5 w-5" />
          {unreadCount > 0 && (                            /* ← dot + number */
            <span className="
            absolute -right-1 -top-1
            bg-red text-white text-xs leading-none
            rounded-full min-w-[18px] h-[18px] px-[4px] flex items-center justify-center
          ">
              {unreadCount}
            </span>
          )}
        </button>
      </div>
    </header>
  )
}


// 3) Slide-over FilterPanel
function FilterPanel({
  isOpen,
  onClose,
  driverFilter,
  dispatcherFilter,
  dateRange,
  setDateRange,
  clearAll
}) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <div className="fixed inset-0 z-30">
        <div
          className="absolute inset-0 bg-black/10"
          onClick={() => { clearAll(); onClose() }}
        />

        <div className="absolute top-16 right-0 w-96 bg-white border-l border-gray-200 shadow-lg rounded-l-lg p-6 flex flex-col space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Filter Orders</h3>
            <button
              onClick={() => { clearAll(); onClose() }}
              className="p-1 rounded hover:bg-gray-100 transition"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="flex flex-col space-y-4 overflow-y-visible">
            <SelectDropdown
              placeholder="By driver…"
              options={driverFilter.options}
              value={driverFilter.value}
              onChange={driverFilter.onChange}
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />

            <SelectDropdown
              placeholder="By dispatcher…"
              options={dispatcherFilter.options}
              value={dispatcherFilter.value}
              onChange={dispatcherFilter.onChange}
              isClearable
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                <DatePicker
                  selected={dateRange.start}
                  onChange={d => setDateRange(r => ({ ...r, start: d }))}
                  className="w-full border rounded-lg p-2 focus:border-[#0A84FF] focus:outline-none"
                  placeholderText="Start date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <DatePicker
                  selected={dateRange.end}
                  onChange={d => setDateRange(r => ({ ...r, end: d }))}
                  className="w-full border rounded-lg p-2 focus:border-[#0A84FF] focus:outline-none"
                  placeholderText="End date"
                />
              </div>
            </div>
          </div>

          <div className="mt-auto flex justify-between space-x-2">
            <button
              onClick={clearAll}
              className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Reset
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-[#0A84FF] text-white rounded-lg hover:bg-blue-600 transition"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </Transition>
  )
}

// 4) Sticky Map preview
function MapPreview({ coords, allCoords }) {
  const list = coords
    ? [`${coords.lat},${coords.lng}`]
    : allCoords.map(c => `${c.lat},${c.lng}`);

  const zoom = coords ? 15 : 5;
  const qParam = list.join('|');

  return (
    <div className="sticky top-20 h-full bg-gray-100 rounded-xl shadow-inner overflow-hidden">
      <iframe
        title="map"
        src={`https://maps.google.com/maps?q=${qParam}&z=${zoom}&output=embed`}
        className="block w-full h-full border-0"
      />
    </div>
  );
}




export default function DispatchScreen() {
  // ─── your ORIGINAL state hooks ───────────────────────────────
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [driverFilterValue, setDriverFilterValue] = useState(null);
  const [dispatcherFilterValue, setDispatcherFilterValue] = useState(null);
  const [deliverySort, setDeliverySort] = useState('asc');
  const [createdSort, setCreatedSort] = useState('desc');
  const [driverSort, setDriverSort] = useState(null);
  const [lastClicked, setLastClicked] = useState('delivery');
  const [adminUsers, setAdminUsers] = useState([]);
  // ─── NEW UI state ─────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [hoveredCoords, setHoveredCoords] = useState(null);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [lastCompletedFetch, setLastCompletedFetch] = useState(null);
  const [didFetchDrivers, setDidFetchDrivers] = useState(false);
  const [didFetchProperties, setDidFetchProperties] = useState(false);
  // flags so we only fetch once per session
  const [hasFetchedCurrent, setHasFetchedCurrent] = useState(false);
  const [hasFetchedCompleted, setHasFetchedCompleted] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');


  useLockBodyScroll(expandedOrderId !== null || chatOpen);

  const { adminInfo } = useSelector(s => s.login);
  const adminId = adminInfo?._id;

  const refreshOrders = updated => {
    setOrders(os => os.map(o => o._id === updated._id ? updated : o));
  };

  /* listens once for all conversations that involve me */
  useEffect(() => {
    if (!adminId) return;

    const convQuery = query(
      collection(db, "conversations"),
      where("participants", "array-contains", adminId)
    );


    const unsubscribe = onSnapshot(convQuery, snapshot => {
      // sum up every convo’s unreadCount
      const total = snapshot.docs.reduce((sum, doc) => {
        const c = doc.data().unreadCount;
        return sum + (typeof c === "number" ? c : 0);
      }, 0);
      setUnreadCount(total);
    }, err => console.error("Conversation listener error:", err));

    return unsubscribe;
  }, [adminId]);


  const fetchDrivers = async () => {
    if (didFetchDrivers) return;
    try {
      const res = await request.get('/drivers');
      setDidFetchDrivers(true);
      const mapped = res.data.map(d => ({
        id: d._id,
        name: `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || 'Unnamed Driver',
        email: d.email,
        phone: d.phoneNumber,
        currentAddress: d.currentAddress,
      }));
      setDrivers(mapped);
    } catch (err) {
      console.error('Error fetching drivers', err);
    }
  };

  // const fetchProperties = async () => {
  //   try {
  //     const { data } = await request.get('/properties', {
  //       params: { limit: 0 }  // 0 → no pagination
  //     });
  //     setProperties(data);
  //   } catch (err) {
  //     console.error('Error fetching properties:', err);
  //   }
  // };


  const fetchHostsAndAdmins = async () => {
    try {
      const res = await request.get('/hosts');
      const allHosts = res.data || [];

      setHosts(allHosts);
      setAdminUsers(allHosts.filter(u => u.isAdmin));
    } catch (err) {
      console.error('Error fetching hosts and admins', err);
    }
  };
  // ─── your ORIGINAL fetch functions ────────────────────────────
  // const fetchAllOrders = async () => {
  //   try {
  //     // ────── 1. Fetch current orders ──────
  //     const { data: currentOrders = [] } = await request.get('/orders', {
  //       params: { limit: 100 }
  //     });

  //     // Sort once (descending delivery date)
  //     const sortedCurrent = [...currentOrders].sort(
  //       (a, b) => new Date(b.inStockDate) - new Date(a.inStockDate)
  //     );

  //     setOrders(sortedCurrent);

  //     // ────── 2. Fetch completed orders (paginated + deduped) ──────
  //     if (activeTab === 4) {
  //       // 2. Fetch completed orders (paginated + deduped)
  //       const params = {
  //         page: 1,
  //         limit: 100,
  //       };
  //       if (lastCompletedFetch) {
  //         params.since = lastCompletedFetch;
  //       }

  //       const { data: completed = [] } = await request.get('/orders/completed', { params });

  //       if (completed.length > 0) {
  //         setCompletedOrders(prev => {
  //           const combined = [...prev, ...completed];
  //           const deduped = Array.from(new Map(combined.map(o => [o._id, o])).values());
  //           return deduped;
  //         });

  //         const latestCreatedAt = completed.reduce((latest, o) => {
  //           const created = new Date(o.createdAt).toISOString();
  //           return created > latest ? created : latest;
  //         }, lastCompletedFetch || '');

  //         setLastCompletedFetch(latestCreatedAt);
  //       }
  //     }
  //   } catch (err) {
  //     console.error('Error fetching completed orders:', err);
  //   }
  // };




  // fetch just the “current” orders
  const fetchCurrent = async () => {
    try {
      const { data: current = [] } = await request.get('/orders', {
        params: { status: 'pending,requested,picked,paymentCompleted', limit: 100 }
      });
      const sorted = [...current].sort(
        (a, b) => new Date(b.inStockDate) - new Date(a.inStockDate)
      );
      setOrders(sorted);
      setHasFetchedCurrent(true);
    } catch (err) {
      console.error('Error fetching current orders:', err);
    }
  };

  // fetch just the “completed” orders, with pagination + dedupe
  const fetchCompleted = async () => {
    try {
      const params = { page: 1, limit: 100 };
      if (lastCompletedFetch) params.since = lastCompletedFetch;
      const { data: completed = [] } = await request.get(
        '/orders/completed',
        { params }
      );
      if (completed.length > 0) {
        setCompletedOrders(prev => {
          const all = [...prev, ...completed];
          return Array.from(new Map(all.map(o => [o._id, o])).values());
        });
        const latest = completed.reduce((max, o) => {
          const d = new Date(o.createdAt).toISOString();
          return d > max ? d : max;
        }, lastCompletedFetch || '');
        setLastCompletedFetch(latest);
      }
      setHasFetchedCompleted(true);
    } catch (err) {
      console.error('Error fetching completed orders:', err);
    }
  };



  // ─── persist seenOrders ───────────────────────────────────────
  const [seenOrders, setSeenOrders] = useState(() => {
    const stored = localStorage.getItem('seenOrders') || '[]';
    return new Set(JSON.parse(stored));
  });
  useEffect(() => {
    localStorage.setItem('seenOrders', JSON.stringify(Array.from(seenOrders)));
  }, [seenOrders]);

  // ─── initial + polling load ──────────────────────────────────
  useEffect(() => {
    // drivers (guarded internally by didFetchDrivers)
    fetchDrivers();

    // hosts + admin users
    fetchHostsAndAdmins();

    // properties: only once
    if (!didFetchProperties || !hasFetchedCompleted) {
      // fetchProperties();
      setDidFetchProperties(true);
    }
  }, []);

  // when the activeTab changes, do a one‑time initial load
  useEffect(() => {
    if (activeTab === 4) {
      if (!hasFetchedCompleted) {
        fetchCompleted();
        setHasFetchedCompleted(true);
      }
    } else {
      if (!hasFetchedCurrent) {
        fetchCurrent();
        setHasFetchedCurrent(true);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if (message) {
      fetchCurrent();
      setMessage('');
    }
  }, [message]);
  // polling every 90s, but only for the active tab
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hidden) return;
      if (activeTab === 4) {
        fetchCompleted();
      } else {
        fetchCurrent();
      }
    }, 70_000);

    return () => clearInterval(interval);
  }, [activeTab, lastCompletedFetch]);



  // ─── your ORIGINAL helpers ───────────────────────────────────
  const isDispatched = (order) => {
    if (order.status === 'completed') return false;

    // if this is an add-on, find its parent in the same `orders` array
    const parent = order.originalOrderId
      ? orders.find(o => String(o._id) === String(order.originalOrderId))
      : order;

    const hasDriver = !!parent?.driverID;
    const hasArrival = !!order.storeArrivalTime;
    const spendNum = parseFloat(order.storeSpend);
    const hasValidSpend = !isNaN(spendNum) && spendNum > 0;
    const hasOrderId = order.storeOrderId?.trim().length > 0;

    return hasDriver && hasArrival && hasValidSpend && hasOrderId;
  };

  // ─── your ORIGINAL tab logic ─────────────────────────────────
  const awaitingDispatch = orders.filter(o =>
    o.status === 'paymentCompleted' &&
    (!o.storeSpend || parseFloat(o.storeSpend) <= 0 ||
      !o.storeOrderId?.trim())
  );
  const submittedOrders = orders.filter(o =>
    o.status === 'paymentCompleted' &&
    parseFloat(o.storeSpend) > 0 &&
    o.storeOrderId?.trim()
  );
  const assignDriverOrders = orders.filter(o => o.status === 'requested');
  const dispatchedOrders = orders.filter(isDispatched);

  const allForGrouping = [...awaitingDispatch, ...submittedOrders, ...assignDriverOrders, ...dispatchedOrders, ...completedOrders];       // include every status
  const familyMap = {};
  allForGrouping.forEach(o => {
    const k = `${o.guestEmail}__${o.propertyID}__${new Date(o.inStockDate)
      .toISOString()
      .slice(0, 10)}`;
    (familyMap[k] ||= []).push(o);
  });

  Object.values(familyMap).forEach(group => {
    if (group.length < 2) return;                               // no siblings → nothing to tag
    const parent = group.reduce(
      (earliest, curr) =>
        new Date(curr.createdAt) < new Date(earliest.createdAt) ? curr : earliest,
      group[0]
    );
    group.forEach(o => {
      o._isAddon = o._id !== parent._id;
      o.originalOrderId = parent._id;
      o.originalUniqueId = parent.uniqueId;                     // handy for the UI cue
    });
  });
  let displayedOrders;
  if (activeTab === 0) displayedOrders = awaitingDispatch;
  else if (activeTab === 1) displayedOrders = submittedOrders;
  else if (activeTab === 2) displayedOrders = assignDriverOrders;
  else if (activeTab === 4) displayedOrders = completedOrders;
  else displayedOrders = dispatchedOrders;



  //NEW SEARCH
  // ─── GLOBAL SEARCH FILTER (with safe defaults) ─────────────────
  if (globalSearch) {
    const q = globalSearch.toLowerCase();
    displayedOrders = displayedOrders.filter(o => {
      // safe lookups
      const hostRec = hosts.find(h => h._id === o.property?.hostID) || {};
      const driverRec = drivers.find(d => d.id === o.driverID) || {};

      // flatten address bits, ignore undefined
      const address = [
        o.property?.address?.line1,
        o.property?.address?.line2,
        o.property?.listingTitle
      ]
        .filter(Boolean)
        .join(' ');

      return (
        (o.uniqueId || '').toLowerCase().includes(q) ||
        (o.guestEmail || '').toLowerCase().includes(q) ||
        (`${hostRec.firstName || ''} ${hostRec.lastName || ''}`)
          .toLowerCase()
          .includes(q) ||
        (driverRec.name || '').toLowerCase().includes(q) ||
        address.toLowerCase().includes(q)
      );
    });
  }



  // ─── SEARCH, FILTER, DATE-RANGE ─────────────────────────────
  if (search) {
    const q = search.toLowerCase();
    displayedOrders = displayedOrders.filter(o =>
      o.uniqueId.toLowerCase().includes(q) ||
      o.property?.address?.line1?.toLowerCase().includes(q)
    );
  }
  if (driverFilterValue) {
    displayedOrders = displayedOrders.filter(o => o.driverID === driverFilterValue.value);
  }
  if (dispatcherFilterValue) {
    displayedOrders = displayedOrders.filter(o => o.dispatcher === dispatcherFilterValue.value);
  }
  if (dateRange.start) {
    displayedOrders = displayedOrders.filter(o => new Date(o.inStockDate) >= dateRange.start);
  }
  if (dateRange.end) {
    displayedOrders = displayedOrders.filter(o => new Date(o.inStockDate) <= dateRange.end);
  }



  // ─── SORT ────────────────────────────────────────────────────
  // ─── distance + proximity helpers ─────────────────────────────
  const haversineKm = (a, b) => {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const toRad = d => (d * Math.PI) / 180;
    const R = 6371;                                     // earth radius [km]
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);

    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

    return 2 * R * Math.asin(Math.sqrt(h));
  };

  // greedy “nearest-neighbour” walk; keeps O(n²) simple + deterministic
  const sortByProximity = list => {
    if (list.length < 3) return list;

    const remaining = [...list];
    const result = [remaining.shift()];                 // start with first
    while (remaining.length) {
      const last = result[result.length - 1];
      const lastPos = last.property?.address;
      let idx = 0;
      let best = Number.POSITIVE_INFINITY;

      remaining.forEach((o, i) => {
        const pos = o.property?.address;
        const d = haversineKm(lastPos, pos);
        if (d < best) {
          best = d;
          idx = i;
        }
      });
      result.push(remaining.splice(idx, 1)[0]);
    }
    return result;
  };

  const getDriverName = id => {
    const d = drivers.find(x => x.id === id);
    return d ? d.name : 'Not Assigned';
  };
  const toggleDriverSort = () => {
    if (driverSort === 'asc') setDriverSort('desc');
    else if (driverSort === 'desc') setDriverSort(null);
    else setDriverSort('asc');
    setLastClicked('driver');
  };

  // ─── SORT ─────────────────────────────────────────────────────

  if (lastClicked === 'driver') {
    displayedOrders.sort((a, b) => {
      // 1) primary: inStockDate using your deliverySort direction
      const dateA = new Date(a.inStockDate).getTime();
      const dateB = new Date(b.inStockDate).getTime();
      const dateCmp = deliverySort === 'asc'
        ? dateA - dateB
        : dateB - dateA;
      if (dateCmp !== 0) return dateCmp;

      // 2) secondary: driver name A→Z or Z→A
      const nameA = getDriverName(a.driverID).toLowerCase();
      const nameB = getDriverName(b.driverID).toLowerCase();
      return driverSort === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });



  } else if (lastClicked === 'delivery') {
    // 1) primary sort by delivery date (asc|desc) as before
    displayedOrders.sort((a, b) =>
      deliverySort === 'asc'
        ? new Date(a.inStockDate) - new Date(b.inStockDate)
        : new Date(b.inStockDate) - new Date(a.inStockDate)
    );

    // 2) within each same-day bucket, re-order by distance so
    //    physically close stops appear next to one another
    const buckets = {};
    displayedOrders.forEach(o => {
      const key = new Date(o.inStockDate).toISOString().slice(0, 10);
      (buckets[key] = buckets[key] || []).push(o);
    });

    displayedOrders = Object.values(buckets)
      .flatMap(dayList => sortByProximity(dayList));
  }
  else {
    displayedOrders.sort((a, b) =>
      createdSort === 'asc'
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt)
    );
  }


  // existing: 1) bucket only displayedOrders
  const groupedMap = {};
  displayedOrders.forEach(o => {
    const key = `${o.guestEmail}__${o.propertyID}__${new Date(o.inStockDate)
      .toISOString().slice(0, 10)}`;
    (groupedMap[key] ||= []).push(o);
  });

  // **new**: tag each displayed order as addon/original using familyMap
  Object.values(groupedMap).forEach(group => {
    // find parent in the full family
    const familyKey = `${group[0].guestEmail}__${group[0].propertyID}__${new Date(group[0].inStockDate)
      .toISOString().slice(0, 10)}`;
    const fullFamily = familyMap[familyKey] || [];
    if (fullFamily.length < 2) return;
    // find earliest in fullFamily
    const parent = fullFamily.reduce((p, o) =>
      new Date(o.createdAt) < new Date(p.createdAt) ? o : p
      , fullFamily[0]);
    // now tag *just* the ones that are actually displayed
    group.forEach(o => {
      o._isAddon = o._id !== parent._id;
      o.originalUniqueId = parent.uniqueId;
    });
    group.sort((a, b) => {
      if (a._id === parent._id) return -1;
      if (b._id === parent._id) return 1;
      // tie‑break by createdAt so add‑ons remain in chronological order
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  });

  // 3) flatten
  const groupedOrders = Object.values(groupedMap).flat();



  const allCoords = groupedOrders
    .map(o => {
      const a = o.property?.address;
      return a && a.lat != null && a.lng != null
        ? { lat: a.lat, lng: a.lng }
        : null;
    })
    .filter(Boolean);



  // ─── toggles ────────────────────────────────────────────────
  const toggleDeliverySort = () => {
    setDeliverySort(s => {
      const next = s === 'asc'
        ? 'desc'
        : s === 'desc'
          ? null
          : 'asc';

      // if we just hit “off,” clear the driver‐sort
      if (next === null) {
        setDriverSort(null);
      }

      return next;
    });
    setLastClicked('delivery');
  };


  const patchOrder = async (id, data) => {
    try { await request.patch(`/orders/${id}/dispatch`, data); setExpandedOrderId(null); }
    catch (e) { console.error('Failed to update order:', e); }
  };

  // 1) a little pastel palette
  const palette = [
    '#fde2e2', // light red
    // '#fef9c3', // light yellow
    // '#d1fae5', // light green
    '#dbeafe', // light blue
    '#ede9fe', // light purple
    '#ffe4e6', // light pink
    '#ccfbf1', // light teal
  ];

  // 2) build a map of ISO‑date → one of those colours
  const dateColorMap = useMemo(() => {
    const dates = Array.from(new Set(
      groupedOrders.map(o =>
        new Date(o.inStockDate).toISOString().slice(0, 10)
      )
    ));
    return dates.reduce((map, date, i) => {
      map[date] = palette[i % palette.length];
      return map;
    }, {});
  }, [groupedOrders]);

//Batching
const lastStops = useMemo(() => {
  const stops = {};
  // displayedOrders is already sorted earliest→latest
  displayedOrders.forEach(o => {
    if (o.driverID) stops[o.driverID] = o;
  });
  return stops;
}, [displayedOrders]);

const driverLoads = useMemo(() => {
  return displayedOrders.reduce((map, o) => {
    if (!o.driverID) return map;
    const qty = (o.products || []).reduce((sum,p)=>sum+(p.quantity||0),0);
    map[o.driverID] = (map[o.driverID] || 0) + qty;
    return map;
  }, {});
}, [displayedOrders]);



// Batching
const batchInfoMap = useMemo(() => {
  const THRESHOLD_KM = 16;
  // group by *date* only
  const byDay = allForGrouping.reduce((m, o) => {
    const day = new Date(o.inStockDate).toISOString().slice(0,10);
    (m[day] ||= []).push(o);
    return m;
  }, {});

  const map = {};
  Object.values(byDay).forEach(dayOrders => {
    dayOrders.forEach((a,i) => {
      for (let j = i+1; j < dayOrders.length; j++) {
        const b = dayOrders[j];
        const dist = haversineKm(a.property.address, b.property.address);
        if (dist <= THRESHOLD_KM) {
          (map[a._id] ||= []).push({ id: b._id, dist });
          (map[b._id] ||= []).push({ id: a._id, dist });
        }
      }
    });
  });

  return map;
}, [groupedOrders]);



  // ─── RENDER ────────────────────────────────────────────────
  return (
    <div className="flex antialiased bg-[#F7F9FC] text-[#0B1220]">
      {/* <Sidebar/> */}
      <div className="px-10 py-6 font-sans flex h-screen flex-col w-full">
        <TopBar
          search={search}
          setSearch={setSearch}
          onFilterClick={() => setShowFilter(true)}
          onChatClick={() => setChatOpen(true)}
          unreadCount={unreadCount}
        />
  
        <FilterPanel
          isOpen={showFilter}
          onClose={() => setShowFilter(false)}
          driverFilter={{
            options: drivers.map(d => ({ label: d.name, value: d.id })),
            value: driverFilterValue,
            onChange: setDriverFilterValue
          }}
          dispatcherFilter={{
            options: adminUsers.map(u => ({
              label: `${u.firstName} ${u.lastName}`,
              value: `${u.firstName} ${u.lastName}`
            })),
            value: dispatcherFilterValue,
            onChange: setDispatcherFilterValue
          }}
          dateRange={dateRange}
          setDateRange={setDateRange}
          clearAll={() => { setDriverFilterValue(null); setDispatcherFilterValue(null); setDateRange({ start: null, end: null }); }}
        />
  
        <Transition show={chatOpen} as={Fragment}>
          {/* backdrop behind the panel */}
          <div
            className="fixed inset-0 z-20 bg-[#0B1220] bg-opacity-30"
            onClick={() => setChatOpen(false)}
          />
  
          {/* lift ChatPanel above the backdrop */}
          <div className="relative z-30">
            <ChatPanel
              isOpen={chatOpen}
              onClose={() => setChatOpen(false)}
              order={null}
              drivers={drivers}
            />
          </div>
        </Transition>
  
        <div className="px-10 py-6 font-sans flex">
          <div
            className="flex-1 overflow-y-auto"
            style={{ maxHeight: 'calc(100vh - 6rem)' }}
          >
            {/* Tabs */}
            <div className="flex-1">
              <Tab.Group
                selectedIndex={activeTab}
                onChange={i => {
                  setActiveTab(i);
                  setExpandedOrderId(null);
                  fetchCurrent();
                  // fetchAllOrders();
                  // if (i === 4 && !completedOrders.length) {
                  //   fetchCompleted();
                  // } else {
                  //   fetchOrders();
                  // }
                }}
              >
  
                <Tab.Group
                  selectedIndex={activeTab}
                  onChange={i => {
                    setActiveTab(i);
                    setExpandedOrderId(null);
  
                    // keep your fetch logic intact
                    // if (i === 4 && !completedOrders.length) {
                    //   fetchCompleted();
                    // } else {
                    //   fetchOrders();
                    // }
                  }}
                >
                  {/* ─────────── Tab bar ─────────── */}
                  <Tab.List
                    className="
                      inline-flex items-center gap-1
                      p-1
                      rounded-full
                      bg-[#FAFBFF]
                      shadow-[0_2px_10px_#0B12200D]
                      ring-1 ring-[#E5EAF0]
                      mb-6
                    "
                  >
                    {[
                      'Awaiting Dispatch',
                      'Awaiting Driver Pay',
                      'Assign Driver',
                      'Dispatched Orders',
                      'Completed',
                    ].map(label => (
                      <Tab
                        key={label}
                        className={({ selected }) => `
                          relative
                          px-3.5 py-2
                          text-[14px] font-semibold leading-none
                          rounded-full
                          transition
                          focus:outline-none
                          focus-visible:ring-2
                          focus-visible:ring-offset-2
                          focus-visible:ring-[#0A84FF]
                          ${selected
                            ? 'bg-[#0A84FF] text-[#FFFFFF] shadow-[0_8px_24px_#0B12201A]'
                            : 'text-[#374151] hover:bg-[#0B12200D]'}
                        `}
                      >
                        {label}
                      </Tab>
                    ))}
                  </Tab.List>
  
                  {/* If you’re using Tab.Panels, leave them below as-is */}
                  {/* <Tab.Panels>…</Tab.Panels> */}
                </Tab.Group>
  
  
              </Tab.Group>
  
              {/* Controls */}
              <div className="flex flex-wrap gap-4 mb-6 items-center">
                <div className="relative w-96">
                  <input
                    type="search"
                    value={globalSearch}
                    onChange={e => setGlobalSearch(e.target.value)}
                    placeholder="Search orders, guests, hosts, drivers or address…"
                    className="
                      w-full h-11 pl-11 pr-4
                      bg-[#0B12200D] backdrop-blur
                      border border-[#E5EAF0]
                      rounded-[12px] shadow-[0_2px_10px_#0B12200D]
                      placeholder-[#6B7280]
                      focus:outline-none focus:ring-2 focus:ring-[#0A84FF]
                      transition
                    "
                  />
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] select-none">
                    🔍
                  </span>
                </div>
  
                <div style={{ display: 'flex', gap: '24px', alignItems: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, Oxygen, Ubuntu, Cantarell, \"Open Sans\", \"Helvetica Neue\", sans-serif' }}>
                  {/* Urgency Sort Toggle */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <button
                      onClick={toggleDeliverySort}
                      aria-pressed={deliverySort !== null}
                      aria-label="Toggle delivery date sort"
                      style={{
                        width: '54px',
                        height: '32px',
                        borderRadius: '16px',
                        backgroundColor: deliverySort ? '#22C55E' : '#E5EAF0',
                        border: '1.5px solid #D0D8E2',
                        position: 'relative',
                        cursor: 'pointer',
                        boxShadow: deliverySort ? '0 8px 24px #0B12201A' : '0 2px 10px #0B12200D',
                        outline: 'none',
                        padding: 0,
                        transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                      }}
                      title="Toggle delivery date sort: Ascending → Descending → Clear"
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '4px',
                          left: deliverySort === 'asc' ? '4px' : deliverySort === 'desc' ? 'calc(100% - 28px)' : 'calc(50% - 12px)',
                          width: '24px',
                          height: '24px',
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          boxShadow: '0 2px 10px #0B12200D',
                          transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      />
                    </button>
                    <span
                      style={{
                        marginTop: '8px',
                        fontWeight: 600,
                        fontSize: '13px',
                        color: '#374151',
                        userSelect: 'none',
                        display: 'inline-block',
                        minWidth: '110px',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {deliverySort === 'asc' && 'Arriving Soon'}
                      {deliverySort === 'desc' && 'Arriving Later'}
                      {deliverySort === null && 'Urgency'}
                    </span>
                  </div>
  
                  {/* Newest Orders Sort Toggle */}
                  {adminInfo.role === 'adminn' &&
  
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <button
                        onClick={() => {
                          if (createdSort === 'asc') setCreatedSort('desc');
                          else if (createdSort === 'desc') setCreatedSort(null);
                          else setCreatedSort('asc');
                          setLastClicked('created');
                        }}
                        aria-pressed={createdSort !== null}
                        aria-label="Toggle order creation date sort"
                        style={{
                          width: '54px',
                          height: '32px',
                          borderRadius: '16px',
                          backgroundColor: createdSort ? '#22C55E' : '#E5EAF0',
                          border: '1.5px solid #D0D8E2',
                          position: 'relative',
                          cursor: 'pointer',
                          boxShadow: createdSort ? '0 8px 24px #0B12201A' : '0 2px 10px #0B12200D',
                          outline: 'none',
                          padding: 0,
                          transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                        }}
                        title="Toggle order creation date sort: Ascending → Descending → Clear"
                      >
                        <span
                          style={{
                            position: 'absolute',
                            top: '4px',
                            left: createdSort === 'asc' ? '4px' : createdSort === 'desc' ? 'calc(100% - 28px)' : 'calc(50% - 12px)',
                            width: '24px',
                            height: '24px',
                            borderRadius: '12px',
                            backgroundColor: '#FFFFFF',
                            boxShadow: '0 2px 10px #0B12200D',
                            transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      </button>
  
                      <span
                        style={{
                          marginTop: '8px',
                          fontWeight: 600,
                          fontSize: '13px',
                          color: '#374151',
                          userSelect: 'none',
                          display: 'inline-block',
                          minWidth: '110px',
                          textAlign: 'center',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {createdSort === 'asc' && 'Oldest Orders'}
                        {createdSort === 'desc' && 'Newest Orders'}
                        {createdSort === null && 'Chronological'}
                      </span>
  
                    </div>
                  }
  
                  {/* Driver Sort Toggle */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <button
                      onClick={toggleDriverSort}
                      disabled={deliverySort === null}
                      aria-pressed={driverSort !== null}
                      aria-label="Toggle driver sort"
                      style={{
                        width: '54px',
                        height: '32px',
                        borderRadius: '16px',
                        backgroundColor: driverSort ? '#22C55E' : '#E5EAF0',
                        opacity: deliverySort === null ? 0.6 : 1,
                        border: '1.5px solid #D0D8E2',
                        position: 'relative',
                        cursor: deliverySort === null ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease-in-out, opacity 0.2s ease-in-out'
                      }}
                      title="Toggle sort by driver: A→Z → Z→A → Clear"
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: '4px',
                          left:
                            driverSort === 'asc'
                              ? '4px'
                              : driverSort === 'desc'
                                ? 'calc(100% - 28px)'
                                : 'calc(50% - 12px)',
                          width: '24px',
                          height: '24px',
                          borderRadius: '12px',
                          backgroundColor: '#FFFFFF',
                          boxShadow: '0 2px 10px #0B12200D',
                          transition: 'left 0.2s cubic-bezier(0.4,0,0.2,1)'
                        }}
                      />
                    </button>
                    <span style={{
                      marginTop: '8px',
                      fontWeight: 600,
                      fontSize: '13px',
                      color: '#374151',
                      userSelect: 'none'
                    }}>
                      {driverSort === 'asc' && 'Driver A→Z'}
                      {driverSort === 'desc' && 'Driver Z→A'}
                      {driverSort === null && 'Sort Driver'}
                    </span>
                  </div>
  
                  {/* Clear Filters */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <button
                      onClick={() => {
                        setDeliverySort(null);
                        setCreatedSort(null);
                        setDriverSort(null);
                        setDriverFilterValue(null);
                        setDispatcherFilterValue(null);
                        setDateRange({ start: null, end: null });
                      }}
                      className="px-4 py-2 rounded-full font-semibold focus:outline-none focus:ring-2 focus:ring-[#0A84FF] transition"
                      style={{
                        backgroundColor: '#FFFFFF',
                        color: '#0B1220',
                        border: '1px solid #E5EAF0',
                        userSelect: 'none',
                        cursor: 'pointer',
                        marginTop: '-22px',
                        boxShadow: '0 2px 10px #0B12200D'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#D6E7FF';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = '#FFFFFF';
                      }}
                      title="Clear all filters"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
  
              {/* List */}
              <ul className="space-y-4" role="list" aria-label="Orders">
                {Object.values(groupedMap).map(group => {
                  const [mainOrder, ...addOns] = group;
                  const iso = new Date(mainOrder.inStockDate).toISOString().slice(0, 10);
                  const base = '#fa531e' || '#ffffff';
  
                  const allOrders = [mainOrder, ...addOns];
                  const totalCount = allOrders
                    .flatMap(o => o.products || [])
                    .reduce((sum, p) => sum + (p.quantity || 0), 0);
                  const isNew = !seenOrders.has(mainOrder._id);
                  const groupKey = mainOrder._id;
                  const isAnyExpanded = expandedOrderId !== null;
                  const isSelected = mainOrder._id === expandedOrderId;
                  const today = new Date()
                  const todayStr = today.toDateString();
                  const tomorrow = new Date(today);
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const tomorrowStr = tomorrow.toDateString();
  
                  // what’s this order’s date?
                  const inDateStr = new Date(mainOrder.inStockDate).toDateString();
  
                  // flags
                  const isToday = activeTab !== 4 && inDateStr === todayStr;
                  const isTomorrow = activeTab !== 4 && inDateStr === tomorrowStr;
                  // pick a background
                  let bg = '#FFFFFF';            // default
                  if (isToday) {
                    bg = '#EAF7EE';               // subtle green
                  } else if (isTomorrow) {
                    bg = '#FFF7E5';               // subtle amber
                  }
                  else {
                    bg = dateColorMap[iso] || '#FFFFFF';
                  }
  
                  const host = hosts.find(h => h._id === mainOrder.property?.hostID) || mainOrder.property.hostID;;
                  const hostName = host.firstName
                    ? `${host.firstName} ${host.lastName}`
                    : 'N/A';
  
                  const current = lastStops[mainOrder.driverID];
                  const load = driverLoads[mainOrder.driverID] || 0;
                  const batchable = canBatch(current, mainOrder, load);
                  
                  return (
                    <React.Fragment key={groupKey}>
                      <li
                        role="listitem"
                        onMouseEnter={() => {
                          const c = { lat: mainOrder.property.address.lat, lng: mainOrder.property.address.lng };
                          setHoveredCoords(c.lat && c.lng ? c : null);
                          setHoveredGroup(groupKey);
                        }}
                        onMouseLeave={() => setHoveredGroup(null)}
                        onClick={() => {
                          const next = isSelected ? null : mainOrder._id;
                          setExpandedOrderId(next);
                          // fetchHostsAndAdmins();
                          if (next) setSeenOrders(s => new Set(s).add(mainOrder._id));
                        }}
                        style={{ backgroundColor: bg }}
                        className={`
                          flex items-center justify-between
                          p-4 rounded-[16px]
                          transition
                          relative cursor-pointer
                          shadow-[0_2px_10px_#0B12200D]
                          hover:shadow-[0_8px_24px_#0B12201A] hover:-translate-y-[1px]
                          ${expandedOrderId === mainOrder._id ? 'opacity-100' : 'opacity-95'}
                          focus-within:ring-2 focus-within:ring-[#0A84FF]
                        `}
                      >
                        <div className="flex-1 space-y-2">
                          <span className="text-[14px] leading-tight text-[#0B1220]">
                            <strong>Order ID:</strong> {mainOrder.uniqueId}
                            <div className="absolute right-4 top-1/4 -translate-y-1/2 flex items-center gap-2">
                              {mainOrder.needsEntryInstructions && (
                                <FiLock
                                  className="h-5 w-5 text-[#F59E0B]"
                                  title="Needs entry instructions"
                                />
                              )}
                              {mainOrder.needsDeliveryTime && (
                                <FiClock
                                  className="h-5 w-5 text-[#0A84FF]"
                                  title="Needs delivery time"
                                />
                              )}
                            </div>
                          </span>
  
                          {
    (() => {
      // grab raw batch candidates
      const raw = batchInfoMap[mainOrder._id] || []
  
      // map to miles & filter out zero-distance
      const list = raw
        .map(({ id, dist }) => ({ id, miles: dist * 0.621371 }))
        .filter(({ miles }) => miles > 0)
        .sort((a, b) => a.miles - b.miles)
  
      // only show if there’s at least one, and order isn’t completed/today
      if (
        !list.length ||
        mainOrder.status === 'completed' ||
        isToday
      ) {
        return null
      }
  
      return (
        <Popover className="relative inline-block mt-1">
          <Popover.Button
            className={`
              inline-flex items-center 
              h-7 gap-2 text-[12px] font-semibold 
              px-3 rounded-full 
              ${bg === '#FFF7E5'
                ? 'bg-[#FFFFFF] text-[#C2410C] border border-[#F59E0B]'
                : 'bg-[#FAFBFF] text-[#0B1220] border border-[#E5EAF0]'}
              hover:bg-[#FFFFFF] transition
              focus:outline-none focus:ring-2 focus:ring-[#0A84FF]
            `}
          >
            <FiChevronRight className="h-3 w-3" />
            {list.length} Batch {list.length > 1 ? 'Opts' : 'Opt'}
          </Popover.Button>
  
          <Transition
            as={Fragment}
            enter="transition ease-in-out duration-200"
            enterFrom="opacity-0 translate-y-0.5"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in-out duration-180"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-0.5"
          >
            <Popover.Panel
              className="
                absolute z-20 mt-2 w-52 
                bg-[#FFFFFF]
                border border-[#E5EAF0]
                shadow-[0_8px_24px_#0B12201A]
                rounded-[12px]
                p-2
                max-h-[240px] overflow-auto
              "
            >
              <h4 className="sticky top-0 bg-[#FFFFFF] text-[12px] font-medium text-[#374151] mb-1 py-1">
                Can Batch With
              </h4>
              <ul className="divide-y divide-[#E5EAF0]">
                {list.map(({ id, miles }) => {
                  const other = allForGrouping.find(o => o._id === id) || {}
                  const label = other.uniqueId || id.slice(-4)
                  return (
                    <li
                      key={id}
                      className="flex justify-between items-center py-2 text-[12px]"
                    >
                      <span className="truncate text-[#0B1220]">{label}</span>
                      <span className="ml-2 text-[#6B7280] tabular-nums">
                        {miles.toFixed(1)} mi
                      </span>
                    </li>
                  )
                })}
              </ul>
            </Popover.Panel>
          </Transition>
        </Popover>
      )
    })()
  }
  
  
  {mainOrder._isAddon && (
    // ensure this lives *after* you compute `bg` above
    <span
      className={`
        inline-flex items-center h-7 gap-2 mt-2 text-[12px] font-medium px-3 rounded-full
        ${
          bg === '#FFF7E5'
            ? 'bg-[#FFFFFF] text-[#B45309] border border-[#F59E0B]'
            : 'bg-[#FAFBFF] text-[#B45309] border border-[#E5EAF0]'
        }
      `}
    >
      Add-On for {mainOrder.originalUniqueId}
    </span>
  )}
  
                          <p className="text-[13px] text-[#374151]">
                            <strong>Property Adress: </strong>
                            {mainOrder.property?.address?.line1}, {mainOrder.property?.address?.line2}, {mainOrder.property?.address?.city} 
    <StatePill state={mainOrder?.property?.address?.state} />
                          </p>
                          <p className="text-[13px] text-[#374151]">
                            <strong>Property Manager:</strong> {hostName}
                          </p>
                          <p className="text-[13px] text-[#374151] tabular-nums">
                            <strong>Arrival Date: </strong>{formatDate(mainOrder.inStockDate)}
                          </p>
  {mainOrder.storeArrivalTime && (
    <p className="text-[13px] text-[#374151] tabular-nums">
      <strong>Store Arrival Time (PST): </strong>
      {arrivalInPst(mainOrder.storeArrivalTime, mainOrder.property?.address)}
    </p>
  )}
                          <p className="text-[13px] text-[#374151]">
                            <strong>Store Location: </strong>{mainOrder.storeLocation}
                          </p>
  
                          {totalCount && (
                            <p className="text-[13px] font-semibold text-[#0A84FF] tabular-nums">
                              <strong>Total Items: </strong>
                              {totalCount}
                            </p>
                          )}
  
                          {mainOrder.storeSpend > 0 && !mainOrder._isAddon && (
                            <p className="text-[13px] font-semibold text-[#22C55E] tabular-nums">
                              <strong>Store Spend: </strong>
                              ${mainOrder.storeSpend.toFixed(2)}
                            </p>
                          )}
  
  {activeTab === 1 &&
                            (mainOrder.status === 'paymentCompleted' || mainOrder.status === 'requested') && (
                              <div onClick={e => e.stopPropagation()}>
                                <ChangeDeliveryFee
                                  entry={mainOrder}
                                  setMessage={setMessage}
                                  driverData={drivers}
                                  className="inline-flex items-center h-8 px-3 text-[12px] rounded-[12px] bg-[#FAFBFF] hover:bg-[#FFFFFF] border border-[#E5EAF0] transition focus:outline-none focus:ring-2 focus:ring-[#0A84FF]"
                                />
                              </div>
                            )
                          }
                          {activeTab === 2 && ['pending', 'requested'].includes(mainOrder.status) ? (
                            <div onClick={e => e.stopPropagation()}>
                              <AssignDriver
                                entry={mainOrder}
                                driverData={drivers}
                                setMessage={setMessage}
                                setError={setError}
                              />
                            </div>
                          ) : (
                            <span className="inline-flex items-center h-7 px-3 text-[12px] rounded-full bg-[#FAFBFF] text-[#0B1220] border border-[#E5EAF0]">
                              {getDriverName(mainOrder.driverID)}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex-shrink-0 flex items-center">
                          <button
                            onClick={() => {
                              const next = mainOrder._id === expandedOrderId ? null : mainOrder._id;
                              setExpandedOrderId(next);
                              if (next) setSeenOrders(s => new Set(s).add(mainOrder._id));
                            }}
                            className="p-2 rounded-[12px] transition focus:outline-none focus:ring-2 focus:ring-[#0A84FF] hover:bg-[#0B12200D]"
                            style={{ minWidth: 40, minHeight: 40 }}
                            aria-label="Toggle details"
                          >
                            <FiChevronRight
                              className={`h-5 w-5 transform ${isSelected ? 'rotate-90' : 'rotate-0'} text-[#6B7280]`}
                            />
                          </button>
                        </div>
                      </li>
  
                      {addOns.map(a => {
                        const hostA = hosts.find(h => h._id === a.property?.hostID) || {};
                        const hostNameA = hostA.firstName
                          ? `${hostA.firstName} ${hostA.lastName}`
                          : 'N/A';
                        const isGroupHovered = hoveredGroup === groupKey;
                        return (
                          <li
                            key={a._id}
                            style={{
                              backgroundColor: '#FFFFFF',
                              borderLeft: `70px solid ${base}`
                            }}
                            className={`
                              flex items-center justify-between p-4 pl-12 rounded-[16px] shadow-[0_2px_10px_#0B12200D]
                              ${isGroupHovered ? 'bg-[#FFECEC]' : 'bg-[#FFFFFF]'}
                              text-[#374151] cursor-default opacity-95
                            `}
                          >
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center justify-between">
                                <span className="text-[14px] font-semibold text-[#0B1220]">
                                  Add-On Order ID: {a.uniqueId}
                                </span>
  
                                <span
                                  className="
                                    inline-flex items-center h-7 gap-2
                                    text-[12px]
                                    bg-[#FAFBFF]
                                    text-[#B45309]
                                    px-3
                                    rounded-full border border-[#E5EAF0]
                                  "
                                >
                                  Add-On for {a.originalUniqueId}
                                </span>
                              </div>
  
                              <p className="text-[13px]">
                                <strong>Property Address: </strong>
                                {a.property?.address?.line1}, {a.property?.address?.city}
                              </p>
                              <p className="text-[13px]">
                                <strong>Property Manager:</strong> {hostNameA}
                              </p>
                              <p className="text-[13px] tabular-nums">
                                <strong>Arrival Date: </strong>
                                {formatDate(a.inStockDate)}
                              </p>
                            </div>
                            
                            <span className="inline-flex items-center h-7 px-3 text-[12px] rounded-full bg-[#FAFBFF] text-[#0B1220] border border-[#E5EAF0]">
                              {getDriverName(a.driverID)}
                            </span>
  
                          </li>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </ul>
  
              {/* Slide-in drawer with clickable backdrop */}
              <Transition appear show={expandedOrderId !== null} as={Fragment}>
                {/* BACKDROP */}
                <div
                  className="fixed inset-0 z-40 bg-[#0B1220] bg-opacity-30"
                  onClick={() => setExpandedOrderId(null)}
                />
  
                {/* PANEL */}
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-200"
                  enterFrom="translate-x-full opacity-0"
                  enterTo="translate-x-0 opacity-100"
                  leave="transform transition ease-in-out duration-200"
                  leaveFrom="translate-x-0 opacity-100"
                  leaveTo="translate-x-full opacity-0"
                >
                  <div
                    className="fixed inset-y-0 right-0 z-50 w-[600px] bg-[#FFFFFF] h-full p-6 overflow-auto pointer-events-auto rounded-l-[16px] border-l border-[#E5EAF0] shadow-[0_8px_24px_#0B12201A]"
                    onClick={e => e.stopPropagation()}
                  >
                    <div className="sticky top-0 bg-[#FFFFFF] pb-3 mb-4 border-b border-[#E5EAF0] flex justify-end">
                      <button
                        onClick={() => setExpandedOrderId(null)}
                        className="text-[#374151] hover:text-[#0B1220] transition focus:outline-none focus:ring-2 focus:ring-[#0A84FF] px-3 py-2 rounded-[12px]"
                      >
                        Close
                      </button>
                    </div>
                    {groupedOrders
                      .filter(o => o._id === expandedOrderId)
                      .map(o => {
                        const key = `${o.guestEmail}__${o.propertyID}__${new Date(o.inStockDate)
                          .toISOString()
                          .slice(0, 10)}`;
  
                        const group = groupedMap[key] || [];
  
                        if (group.length === 0) {
                          // nothing to render if somehow the group is empty
                          return null;
                        }
  
                        const mainOrder = group[0];
                        const addons = group.slice(1);
  
                        return (
                          <DispatchOrderDrawer
                            key={mainOrder._id}
                            order={mainOrder}
                            isOpen={mainOrder._id === expandedOrderId}
                            addons={addons}
                            refreshOrders={refreshOrders}
                            closeDrawer={() => setExpandedOrderId(null)}
                            drivers={drivers}
                            properties={properties}
                            patchOrder={patchOrder}
                            showBroadcast={activeTab === 2}
                          />
                        );
                      })}
  
                  </div>
                </Transition.Child>
              </Transition>
  
            </div>
          </div>
          {/* Map */}
          <div className="w-1/3 ml-4">
            <div className="relative rounded-[16px] bg-[#FFFFFF] border border-[#E5EAF0] shadow-[0_2px_10px_#0B12200D] overflow-hidden h-[500px]">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[linear-gradient(180deg,#0A84FF,#6366F1)]" />
              <MapPreview
                coords={hoveredCoords}
                allCoords={allCoords}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  
}

// ─── Placeholder icons ─────────────────────────────────────
function DashboardIcon() { return <span className="block w-6 h-6 bg-gray-500 rounded-full" />; }
function UsersIcon() { return <span className="block w-6 h-6 bg-gray-500 rounded-full" />; }
function TruckIcon() { return <span className="block w-6 h-6 bg-gray-500 rounded-full" />; }
function ClipboardIcon() { return <span className="block w-6 h-6 bg-gray-500 rounded-full" />; }
function ReceiptIcon() { return <span className="block w-6 h-6 bg-gray-500 rounded-full" />; }
function OfferIcon() { return <span className="block w-6 h-6 bg-gray-500 rounded-full" />; }
